import { useEffect, useState, type DependencyList } from "react";
import type { PostgrestError } from "@supabase/supabase-js";

interface QueryState<T> {
  data: T | null;
  error: PostgrestError | Error | null;
  isLoading: boolean;
}

/**
 * Runs a Supabase query on mount and whenever `deps` changes, tracking
 * loading/error/data state and guarding against setting state after the
 * component has unmounted (e.g. user navigates away mid-request).
 *
 * This intentionally does not cache or dedupe across components — for a
 * foundation this keeps behaviour obvious. If query caching/invalidation
 * becomes a real need, that's a deliberate addition, not a default.
 */
export function useSupabaseQuery<T>(
  queryFn: () => Promise<{ data: T | null; error: PostgrestError | Error | null }>,
  deps: DependencyList,
): QueryState<T> {
  const [state, setState] = useState<QueryState<T>>({
    data: null,
    error: null,
    isLoading: true,
  });

  useEffect(() => {
    let isMounted = true;
    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    queryFn()
      .then(({ data, error }) => {
        if (!isMounted) return;
        setState({ data, error, isLoading: false });
      })
      .catch((error: Error) => {
        if (!isMounted) return;
        setState({ data: null, error, isLoading: false });
      });

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return state;
}
